import { CachedItem } from "@lib-shared/lib";
import { appSettings } from "core/config";
import { FinancialPermissions } from "core/constants";
import { logApiError } from "core/lib";
import User, { getUser } from "core/models/user";
import { ApproveUserRequest, InviteUserRequest, UserPermissionsRequest } from "core/models/user-permissions.request";
import { PermissionService } from "core/services/cef";
import { CustomerAgreementWithPermissions, UpdateUserAgreementsRequest } from "core/services/cef/permission.service.types";
import { Permission } from "core/services/cef/types";
import UserDetailsWrapper from "self-service/users/management/user-details-wrapper";

const permissionsCache = new CachedItem("permissions.all", true);

export type MappedUser = {
    firstName: any,
    lastName: any,
    ciam: any,
    friendlyName: any,
    permissions: any,
    hasFinancialAccess: any,
    email: any,
    phone: any,
    role: any,
    agreementIds: number[]
}

const permissionService = new PermissionService();

const UserPermissionsManager = {
    inviteLink: appSettings.urls.acceptChildInvite,

    getUser: () => User.instance ?? getUser(),

    refreshUser: () => {
        return getUser();
    },

    hasParent: () => {
        const user = UserPermissionsManager.getUser();

        if (!user) {
            return;
        }

        return !user?.isPrimaryUser ?? false;
    },

    getPermissions: async () => {
        if (permissionsCache.isCached) {
            return permissionsCache.value;
        }

        const permissions = (await permissionService.getPermissions()).data.permissions;

        permissionsCache.set(permissions);

        return permissions;
    },


    getFilteredPermissions: async (grantValues: number[]) => {
        const permissions: Permission[] = await UserPermissionsManager.getPermissions();

        return permissions.filter(x => grantValues.includes(x.grantValue));
    },

    inviteChildUser: async (inviteRequest: InviteUserRequest) => {
        try {
            const request = await new UserPermissionsRequest().invite(inviteRequest);
            return await permissionService.addChildUser(request, UserPermissionsManager.inviteLink);
        }
        catch (error) {
            logApiError(error);
            throw error;
        }
    },

    registerChildUser: async (customerNumber: number) => {
        try {
            const request = new UserPermissionsRequest().registerChild(customerNumber);
            return await permissionService.addChildUser(request);
        }
        catch (error) {
            logApiError(error);
            throw error;
        }
    },

    updateChildUserAgreements: async (user: UserDetailsWrapper, grantValues: number[]) => {
        const permissions = await UserPermissionsManager.getFilteredPermissions(grantValues);

        const agreements: CustomerAgreementWithPermissions[] = [];

        for (let i = 0; i < user.user.agreements.length; i++) {
            const a = user.user.agreements[i];

            const agreement: CustomerAgreementWithPermissions = {
                friendlyName: a.friendlyName,
                id: a.id,
                permissions: permissions
            };

            agreements.push(agreement);
        }

        const request: UpdateUserAgreementsRequest = {
            childCiamId: user.ciam,
            customerNumber: user.customerNumber,
            agreements: agreements
        }

        try {
            return await permissionService.updateChildUserAgreements(request, user);
        }
        catch (error) {
            logApiError(error);
            throw error;
        }
    },

    approveChildUser: async (approveRequest: ApproveUserRequest) => {
        try {
            const request = await new UserPermissionsRequest().approval(approveRequest);
            return await permissionService.updateUserViaUtilityService(request);
        }
        catch (error) {
            logApiError(error);
            throw error;
        }
    },

    userHasFinancialAccess: (data: any) => {
        const agreement = data.agreements[0];

        const grantValues = agreement.permissions?.map((x: { grantValue: string; }) => parseInt(x.grantValue));
        const expectedGrantValues = FinancialPermissions;

        return expectedGrantValues.every(x => grantValues.includes(x));
    }
}

export default UserPermissionsManager;
